const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script';

let loadPromise: Promise<void> | null = null;

type WindowWithGoogle = typeof window & {
  google?: {
    maps?: {
      places?: unknown;
    };
  };
};

type AddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

export type PlaceDetailsResult = {
  address_components?: AddressComponent[];
  formatted_address?: string;
  geometry?: {
    location?: {
      lat(): number;
      lng(): number;
    };
  };
  name?: string;
};

export const loadGoogleMapsPlacesScript = (apiKey: string): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if ((window as WindowWithGoogle).google?.maps?.places) {
    return Promise.resolve();
  }

  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById(
        GOOGLE_MAPS_SCRIPT_ID,
      ) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve());
        existingScript.addEventListener('error', () => {
          loadPromise = null;
          reject(new Error('Failed to load Google Maps script.'));
        });
        return;
      }

      const script = document.createElement('script');
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        apiKey,
      )}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => {
        loadPromise = null;
        reject(new Error('Failed to load Google Maps script.'));
      });
      document.head.appendChild(script);
    });
  }

  return loadPromise;
};

export type ParsedPlaceAddress = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
};

const findComponent = (
  components: AddressComponent[],
  types: string[],
) =>
  components.find((component) =>
    types.every((type) => component.types.includes(type)),
  );

export const parsePlaceResult = (
  place: PlaceDetailsResult,
): ParsedPlaceAddress => {
  const components = place.address_components;
  if (!components) {
    return {};
  }

  const streetNumber = findComponent(components, ['street_number']);
  const route = findComponent(components, ['route']);
  const subpremise = findComponent(components, ['subpremise']);
  const premise = findComponent(components, ['premise']);
  const locality =
    findComponent(components, ['locality']) ??
    findComponent(components, ['postal_town']) ??
    findComponent(components, ['sublocality', 'sublocality_level_1']) ??
    findComponent(components, ['administrative_area_level_3']);
  const adminArea = findComponent(components, ['administrative_area_level_1']);
  const postalCode = findComponent(components, ['postal_code']);
  const postalCodeSuffix = findComponent(components, ['postal_code_suffix']);

  const line1Parts = [streetNumber?.short_name, route?.long_name].filter(
    Boolean,
  );
  let line1 = line1Parts.join(' ');

  if (!line1 && place.name) {
    line1 = place.name;
  }

  const line2Candidate = subpremise?.short_name ?? premise?.short_name;
  const postal =
    postalCode?.long_name && postalCodeSuffix?.short_name
      ? `${postalCode.long_name}-${postalCodeSuffix.short_name}`
      : postalCode?.long_name;

  return {
    ...(line1 ? { line1 } : {}),
    line2: line2Candidate ?? '',
    city: locality?.long_name ?? '',
    state: adminArea?.short_name ?? '',
    postalCode: postal ?? '',
  };
};
